import { forwardRef, useEffect } from "react";
import styles from "./VideoTrimmer.module.css";
import { fetchFile } from "@ffmpeg/util";
const VideoPreview = forwardRef(
  (
    {
      videoUrl,
      trimSettings,
      rotation = 0,
      onVideoLoad,
      onVideoDataLoaded,
      hasVideoPoster,
      className = "",
    },
    ref
  ) => {
    useEffect(() => {
      const video = ref;

      const handleLoadedMetadata = () => {
        if (video.current) {
          onVideoLoad(video.current.duration);
        }
      };

      if (video.current) {
        video.current.addEventListener("loadedmetadata", handleLoadedMetadata);
        return () => {
          video.current?.removeEventListener(
            "loadedmetadata",
            handleLoadedMetadata
          );
        };
      }
    }, [videoUrl, onVideoLoad, ref]);

    // Calculate container dimensions based on rotation
    const isRotated = rotation === 90 || rotation === 270;

    return (
      <div
        className={`${styles.relative} ${styles.bg_black} ${styles.rounded_lg} ${styles.overflow_hidden} ${className}`}
      >
        {/* Container for rotated video content */}
        <div
          className={`${styles.flex} ${styles.justify_center} ${styles.items_center}`}
          style={{
            minHeight: isRotated ? "400px" : "auto",
          }}
        >
          {/* Video content wrapper - only this gets rotated */}
          <div
            className={`${styles.relative}`}
            style={{
              transform: `rotate(${rotation}deg)`,
              transition: "transform 0.3s ease",
              transformOrigin: "center center",
            }}
          >
            <video
              ref={ref}
              src={videoUrl}
              className={`${styles.w_full} ${styles.h_auto} ${styles.max_h_96} ${styles.object_contain}`}
              style={{
                // For rotated portrait videos, ensure they fit properly
                ...(isRotated && {
                  maxWidth: "400px",
                  maxHeight: "600px",
                }),
              }}
              preload="metadata"
              crossOrigin="anonymous"
              onLoadedData={async (e) => {
                if (!hasVideoPoster) {
                  const buffer = await fetchFile(videoUrl);
                  const blob = new Blob([buffer], { type: "video/mp4" });
                  onVideoDataLoaded(blob);
                }
              }}
            />
          </div>
        </div>

        {/* Controls overlay - positioned absolutely so it doesn't rotate */}
        <div
          className={`${styles.absolute} ${styles.bottom_4} ${styles.left_4} ${styles.right_4} ${styles.flex} ${styles.justify_between} ${styles.items_end}`}
        >
          <div
            className={`${styles.bg_black_70} ${styles.backdrop_blur_sm} ${styles.rounded_lg} ${styles.px_3} ${styles.py_2}`}
          >
            {trimSettings.duration > 0 && (
              <div className={`${styles.text_white} ${styles.text_sm}`}>
                <div className={`${styles.flex} ${styles.space_x_4}`}>
                  <span>Start: {trimSettings.startTime.toFixed(1)}s</span>
                  <span>End: {trimSettings.endTime.toFixed(1)}s</span>
                  <span
                    className={`${styles.text_purple_400} ${styles.font_semibold}`}
                  >
                    Duration: {trimSettings.duration.toFixed(1)}
                  </span>
                  {rotation > 0 && (
                    <span className={`${styles.text_blue_400}`}>
                      {rotation}°
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Standard video controls - positioned absolutely so they don't rotate */}
        <video
          className={`${styles.absolute} ${styles.inset_0} ${styles.w_full} ${styles.h_full} ${styles.opacity_0} ${styles.pointer_events_auto}`}
          src={videoUrl}
          controls
          style={{
            background: "transparent",
            pointerEvents: "auto",
          }}
          onTimeUpdate={(e) => {
            // Sync the hidden control video with the visible one
            if (ref && "current" in ref && ref.current) {
              const target = e.target;
              if (
                Math.abs(ref.current.currentTime - target.currentTime) > 0.1
              ) {
                ref.current.currentTime = target.currentTime;
              }
            }
          }}
          onPlay={(e) => {
            if (ref && "current" in ref && ref.current) {
              ref.current.play();
            }
          }}
          onPause={(e) => {
            if (ref && "current" in ref && ref.current) {
              ref.current.pause();
            }
          }}
          onLoadedData={async (e) => {
            if (!hasVideoPoster) {
              const buffer = await fetchFile(videoUrl);
              const blob = new Blob([buffer], { type: "video/mp4" });
              onVideoDataLoaded(blob);
            }
          }}
        />
      </div>
    );
  }
);

VideoPreview.displayName = "VideoPreview";

export default VideoPreview;
